import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { showToast } from '../components/Toast';
import DmrActionItems from '../components/DmrActionItems';
import {
  dmrApi,
  type DmrAttendee,
  type DmrAudience,
  type DmrBooking,
  type DmrPartner,
  type DmrSeasonResponse,
  type DmrSlot,
} from '../api/dmr';

/**
 * Digital Marketing Review calls (#216). Replaces the yearly sign-up
 * spreadsheet:
 *   - FBCs book their partners in three steps (partner → time → who's coming);
 *   - franchisees book, reschedule or cancel their own call;
 *   - marketing runs the schedule, outcomes and setup.
 * Every booking creates the Outlook + Teams invite on the team member's
 * calendar (once Coastal #262's credentials are live).
 */

const NAVY = '#1f2a48';
const MUTED = '#64748b';
const BORDER = '#e2e8f0';

type Tab = 'book' | 'schedule' | 'partners' | 'actions' | 'setup';

function fmtWhen(iso: string, opts: { withDay?: boolean } = {}) {
  const d = new Date(iso);
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  return opts.withDay === false ? time : `${day} · ${time}`;
}

function fmtWeek(week: string) {
  return new Date(`${week}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Monday (YYYY-MM-DD) of the week containing an ISO instant, Pacific time,
 *  matching the server's week buckets. */
function weekOf(iso: string) {
  const pt = new Date(new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const dow = (pt.getDay() + 6) % 7;
  pt.setDate(pt.getDate() - dow);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${pt.getFullYear()}-${p(pt.getMonth() + 1)}-${p(pt.getDate())}`;
}

/** '06:30' Pacific → '6:30 AM PT (9:30 ET)'. Slot times are defined in
 *  Pacific; the team spans both coasts. */
function fmtPt(hhmm: string) {
  const [h, m] = hhmm.split(':').map(Number);
  const f = (hr: number) => `${((hr + 11) % 12) + 1}:${String(m).padStart(2, '0')}`;
  return `${f(h)} ${h < 12 ? 'AM' : 'PM'} PT (${f(h + 3)} ET)`;
}

function errText(e: unknown) {
  return e instanceof Error ? e.message : 'Something went wrong';
}

export default function DmrPage() {
  const seasonQ = useQuery({ queryKey: ['dmr', 'season'], queryFn: dmrApi.season, retry: false });
  const data = seasonQ.data;
  const role = data?.viewer.role;
  const [tab, setTab] = useState<Tab | null>(null);

  if (seasonQ.isLoading) return <Shell>Loading…</Shell>;
  if (seasonQ.error || !data) {
    return (
      <Shell>
        <p style={{ color: MUTED }}>{errText(seasonQ.error) || 'DMR scheduling is not open yet.'}</p>
      </Shell>
    );
  }

  const staff = role !== 'franchisee';
  const active: Tab = tab ?? (role === 'admin' ? 'schedule' : 'book');
  const tabs: Array<[Tab, string]> = [
    ['book', 'Book a call'],
    ['schedule', 'Schedule'],
    ['partners', 'Franchise partners'],
    ['actions', 'Action items'],
    ...(role === 'admin' ? ([['setup', 'Setup']] as Array<[Tab, string]>) : []),
  ];

  return (
    <Shell>
      <h1 style={{ margin: '10px 0 4px', fontSize: '1.5rem', color: NAVY }}>Digital Marketing Review calls</h1>
      <p style={{ color: MUTED, marginTop: 0 }}>
        {data.season.name}: 90-minute calls with the marketing team, Tuesdays and Thursdays from{' '}
        {fmtWeek(data.season.callsStartOn)}.
      </p>
      <InviteBanner data={data} />
      {staff ? (
        <>
          <StatsRow data={data} />
          <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${BORDER}`, margin: '18px 0 16px' }}>
            {tabs.map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                style={{
                  border: 'none',
                  background: 'none',
                  padding: '10px 14px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                  color: active === k ? NAVY : MUTED,
                  borderBottom: active === k ? `3px solid ${NAVY}` : '3px solid transparent',
                  marginBottom: -1,
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {active === 'book' && <BookPanel role={role!} onDone={() => setTab('schedule')} />}
          {active === 'schedule' && <ScheduleBoard role={role!} />}
          {active === 'partners' && <PartnersTable role={role!} />}
          {active === 'actions' && <ActionItemsTab role={role!} />}
          {active === 'setup' && <SetupPanel data={data} />}
        </>
      ) : (
        <MyCall />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '22px 24px 70px', maxWidth: 1200, margin: '0 auto' }}>
      <p style={{ margin: 0 }}>
        <Link to="/" style={{ color: '#2563eb' }}>← Dashboard</Link>
      </p>
      {children}
    </div>
  );
}

function InviteBanner({ data }: { data: DmrSeasonResponse }) {
  const notes: string[] = [];
  if (!data.invites.live && data.viewer.role !== 'franchisee') {
    notes.push(`${data.invites.reason} Bookings are saved, and their invites go out as soon as this is switched on.`);
  }
  if (data.invites.testMode) notes.push('Test site: invites go to the team member only, never to franchisees or FBCs.');
  if (data.season.audience !== 'all' && data.viewer.role === 'admin') {
    notes.push(
      data.season.audience === 'admin'
        ? 'Only marketing can see this page right now. Open it to FBCs from Setup.'
        : 'FBCs can book. Franchisees can’t see this page yet.',
    );
  }
  if (notes.length === 0) return null;
  return (
    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '10px 14px', fontSize: 14 }}>
      {notes.map((n) => <div key={n}>{n}</div>)}
    </div>
  );
}

function StatsRow({ data }: { data: DmrSeasonResponse }) {
  const p = data.progress;
  const r = data.runway;
  if (!p || !r) return null;
  const done = p.completed + p.scheduled;
  const pct = p.goal ? Math.min(100, Math.round((done / p.goal) * 100)) : 0;
  const runwayColor =
    r.runwayWeeks >= data.season.runwayGoalWeeks ? '#15803d' : r.runwayWeeks >= data.season.runwayAlertWeeks ? '#b45309' : '#b91c1c';
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', margin: '16px 0 0', alignItems: 'flex-end' }}>
      <div style={{ flex: '1 1 340px' }}>
        <div style={{ height: 10, background: BORDER, borderRadius: 999, overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${p.goal ? Math.min(100, (p.completed / p.goal) * 100) : 0}%`, background: '#15803d' }} />
          <div style={{ width: `${Math.max(0, pct - (p.goal ? (p.completed / p.goal) * 100 : 0))}%`, background: '#86efac' }} />
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: MUTED }}>
          <strong style={{ color: NAVY }}>{done}</strong> of {p.goal} calls toward the {data.season.goalPct}% goal ({p.partners} partners) ·{' '}
          {p.completed} done, {p.scheduled} booked{p.noShow ? `, ${p.noShow} no-show` : ''}
        </p>
      </div>
      <div style={{ fontSize: 13, color: MUTED }}>
        Booked ahead:{' '}
        <strong style={{ color: runwayColor, fontSize: 16 }}>
          {r.runwayWeeks} week{r.runwayWeeks === 1 ? '' : 's'}
        </strong>{' '}
        (goal {data.season.runwayGoalWeeks}; a week counts at {data.season.weeklyTarget}+ calls)
      </div>
    </div>
  );
}

// ── Booking ────────────────────────────────────────────────────────────────

function useDmrData() {
  const slots = useQuery({ queryKey: ['dmr', 'slots'], queryFn: dmrApi.slots });
  const partners = useQuery({ queryKey: ['dmr', 'partners'], queryFn: dmrApi.partners });
  return { slots, partners };
}

function useInvalidateDmr() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['dmr'] });
}

function inviteToast(invite: { status: string } | null, verb: string) {
  if (!invite || invite.status === 'sent') showToast(`${verb}. The Teams invite is on its way.`);
  else if (invite.status === 'pending') showToast(`${verb}. The invite will go out once calendar invites are switched on.`, { durationMs: 4500 });
  else showToast(`${verb}, but the invite didn't send. Marketing has been flagged.`, { variant: 'error', durationMs: 5000 });
}

/**
 * The three-step booking flow. With `reschedule`, the partner is fixed and
 * the confirm moves the existing booking (same Teams link if the team member
 * stays the same).
 */
function BookPanel({
  role,
  onDone,
  fixedPartner,
  reschedule,
}: {
  role: 'admin' | 'fbc' | 'franchisee';
  onDone?: () => void;
  fixedPartner?: DmrPartner;
  reschedule?: DmrBooking;
}) {
  const { slots, partners } = useDmrData();
  const invalidate = useInvalidateDmr();
  const [partnerKey, setPartnerKey] = useState<string | null>(fixedPartner?.key ?? reschedule?.partnerKey ?? null);
  const [slotId, setSlotId] = useState<number | null>(null);
  const [extras, setExtras] = useState<DmrAttendee[] | null>(reschedule ? reschedule.extraAttendees : null);
  const [inviteFbc, setInviteFbc] = useState(reschedule ? reschedule.inviteFbc : true);
  const [primaryEmail, setPrimaryEmail] = useState('');

  const partner = partners.data?.partners.find((p) => p.key === partnerKey) ?? fixedPartner ?? null;
  const slot = slots.data?.slots.find((s) => s.id === slotId) ?? null;

  const book = useMutation({
    mutationFn: async () => {
      const attendees = (extras ?? []).filter((a) => a.email.trim());
      if (reschedule) return dmrApi.update(reschedule.id, { slotId: slotId!, extraAttendees: attendees, inviteFbc });
      return dmrApi.book({
        slotId: slotId!,
        partnerKey: partner!.key,
        extraAttendees: attendees,
        inviteFbc,
        primaryEmail: primaryEmail.trim() || undefined,
      });
    },
    onSuccess: (res) => {
      inviteToast(res.invite, reschedule ? 'Rescheduled' : 'Booked');
      invalidate();
      setSlotId(null);
      if (!reschedule && !fixedPartner) setPartnerKey(null);
      onDone?.();
    },
    onError: (e) => {
      showToast(errText(e), { variant: 'error', durationMs: 5000 });
      invalidate();
    },
  });

  // Co-owners default onto the invite; they can be removed like any extra.
  const choosePartner = (p: DmrPartner) => {
    setPartnerKey(p.key);
    setExtras(p.coOwnerEmails.map((email) => ({ name: '', email })));
    setInviteFbc(true);
    setPrimaryEmail(p.email ?? '');
  };

  if (slots.isLoading || partners.isLoading) return <p style={{ color: MUTED }}>Loading times…</p>;

  const needsPartnerStep = !fixedPartner && !reschedule;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {needsPartnerStep && (
        <Step n={1} title="Which franchise partner?" done={partner ? `${partner.name} (${partner.afs.join(', ')})` : null} onEdit={() => setPartnerKey(null)}>
          <PartnerPicker role={role} partners={partners.data?.partners ?? []} onPick={choosePartner} />
        </Step>
      )}
      {(partner || !needsPartnerStep) && (
        <Step
          n={needsPartnerStep ? 2 : 1}
          title={reschedule ? 'Pick the new time' : 'Pick a time'}
          done={slot ? `${fmtWhen(slot.startsAt)} with ${slot.teamMember.name}` : null}
          onEdit={() => setSlotId(null)}
        >
          <SlotPicker slots={slots.data?.slots ?? []} onPick={(s) => setSlotId(s.id)} />
        </Step>
      )}
      {partner && slot && (
        <Step n={needsPartnerStep ? 3 : 2} title="Who's attending?" done={null}>
          <div style={{ display: 'grid', gap: 12, maxWidth: 640 }}>
            {!reschedule && (
              <label style={{ fontSize: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{partner.name}’s email</div>
                <input value={primaryEmail} onChange={(e) => setPrimaryEmail(e.target.value)} placeholder="name@amadaseniorcare.com" style={input} />
              </label>
            )}
            <AttendeesEditor value={extras ?? []} onChange={setExtras} />
            {partner.fbc && role !== 'franchisee' && (
              <label style={{ fontSize: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={inviteFbc} onChange={(e) => setInviteFbc(e.target.checked)} />
                Invite the FBC ({partner.fbc.name})
              </label>
            )}
            <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>
              The invite comes from {slot.teamMember.name}’s Outlook calendar with a Microsoft Teams link, 90 minutes.
            </p>
            <div>
              <button onClick={() => book.mutate()} disabled={book.isPending} style={primaryBtn}>
                {book.isPending ? 'Booking…' : reschedule ? 'Move the call' : 'Book the call'}
              </button>
            </div>
          </div>
        </Step>
      )}
    </div>
  );
}

function Step({
  n,
  title,
  done,
  onEdit,
  children,
}: {
  n: number;
  title: string;
  done: string | null;
  onEdit?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 16px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 24, height: 24, borderRadius: 999, background: done ? '#15803d' : NAVY, color: '#fff', fontSize: 13, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
          {done ? '✓' : n}
        </span>
        <strong style={{ color: NAVY }}>{title}</strong>
        {done && (
          <>
            <span style={{ color: MUTED, fontSize: 14 }}>{done}</span>
            {onEdit && (
              <button onClick={onEdit} style={linkBtn}>
                change
              </button>
            )}
          </>
        )}
      </div>
      {!done && <div style={{ marginTop: 12 }}>{children}</div>}
    </section>
  );
}

function PartnerPicker({
  role,
  partners,
  onPick,
}: {
  role: 'admin' | 'fbc' | 'franchisee';
  partners: DmrPartner[];
  onPick: (p: DmrPartner) => void;
}) {
  const hasMine = role === 'fbc' && partners.some((p) => p.mine);
  const [mineOnly, setMineOnly] = useState(hasMine);
  const [q, setQ] = useState('');
  const list = partners.filter((p) => {
    if (mineOnly && !p.mine) return false;
    if (!q.trim()) return true;
    const hay = `${p.name} ${p.afs.join(' ')} ${p.marketingNames.join(' ')} ${p.fbc?.name ?? ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });
  // Unbooked first: that's who you're here to book.
  list.sort((a, b) => Number(!!a.booking) - Number(!!b.booking) || a.name.localeCompare(b.name));
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, AF number or city…" style={{ ...input, maxWidth: 320 }} />
        {hasMine && (
          <label style={{ fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
            Only my partners
          </label>
        )}
      </div>
      <div style={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${BORDER}`, borderRadius: 8 }}>
        {list.slice(0, 200).map((p) => {
          const booked = p.booking && p.booking.status !== 'cancelled';
          return (
            <button
              key={p.key}
              disabled={!!booked}
              onClick={() => onPick(p)}
              style={{
                display: 'flex', width: '100%', justifyContent: 'space-between', gap: 12, textAlign: 'left',
                padding: '9px 12px', border: 'none', borderBottom: '1px solid #f1f5f9',
                background: booked ? '#f8fafc' : '#fff', cursor: booked ? 'default' : 'pointer', fontSize: 14,
              }}
            >
              <span>
                <strong style={{ color: booked ? MUTED : NAVY }}>{p.name}</strong>{' '}
                <span style={{ color: MUTED }}>{p.afs.join(', ')}</span>
                {p.marketingNames[0] && <span style={{ color: MUTED }}> · {p.marketingNames[0]}</span>}
              </span>
              <span style={{ color: MUTED, whiteSpace: 'nowrap' }}>
                {booked
                  ? p.booking!.status === 'completed'
                    ? 'Call done'
                    : p.booking!.startsAt
                      ? `Booked ${fmtWhen(p.booking!.startsAt)}`
                      : 'Booked'
                  : p.fbc?.name ?? ''}
              </span>
            </button>
          );
        })}
        {list.length === 0 && <div style={{ padding: 14, color: MUTED }}>No partners match.</div>}
      </div>
    </div>
  );
}

/** Pacific calendar date (YYYY-MM-DD) of a slot, which is what decides its
 *  Tuesday/Thursday, matching how the slots were created. */
function ptDay(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
}

/** Tuesday and Thursday look different at a glance. Anything else falls back to slate. */
const DAY_THEME: Record<string, { band: string; tint: string }> = {
  Tuesday: { band: '#1f2a48', tint: '#eef2ff' },
  Thursday: { band: '#0f766e', tint: '#ecfdf5' },
};

function dayName(day: string) {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
}

function DayHeader({ day, right }: { day: string; right?: React.ReactNode }) {
  const name = dayName(day);
  const theme = DAY_THEME[name] ?? { band: '#475569', tint: '#f8fafc' };
  return (
    <div style={{ background: theme.band, color: '#fff', padding: '10px 14px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '.02em' }}>{name}</span>
      <span style={{ fontSize: 14, opacity: 0.85 }}>
        {new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' })}
      </span>
      {right && <span style={{ marginLeft: 'auto', fontSize: 13, opacity: 0.9 }}>{right}</span>}
    </div>
  );
}

function dayTint(day: string) {
  return (DAY_THEME[dayName(day)] ?? { tint: '#f8fafc' }).tint;
}

/** The week picker: "Week of Jan 11" with its call days and a count. */
function WeekChips({
  weeks,
  current,
  onPick,
}: {
  weeks: Array<{ week: string; days: string[]; detail: string }>;
  current: string;
  onPick: (w: string) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
      {weeks.map((w) => {
        const on = w.week === current;
        const days = w.days
          .map((d) => {
            const dt = new Date(`${d}T12:00:00Z`);
            return `${dt.toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' })} ${dt.getUTCDate()}`;
          })
          .join(' & ');
        return (
          <button
            key={w.week}
            onClick={() => onPick(w.week)}
            style={{
              border: on ? `2px solid ${NAVY}` : '1px solid #cbd5e1', background: on ? NAVY : '#fff', color: on ? '#fff' : NAVY,
              borderRadius: 10, padding: '7px 12px', cursor: 'pointer', textAlign: 'left', minWidth: 132,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14 }}>Week of {fmtWeek(w.week)}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{days}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{w.detail}</div>
          </button>
        );
      })}
    </div>
  );
}

function TimeZoneNote() {
  // The generic name ("Pacific Time"), not today's PDT/PST, which would
  // contradict the January slots labelled PST.
  const tz =
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'longGeneric' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  return <p style={{ margin: '0 0 12px', fontSize: 13, color: MUTED }}>Times are shown in your time zone: {tz}.</p>;
}

function groupByDay<T extends { startsAt: string }>(list: T[]) {
  const m = new Map<string, T[]>();
  for (const s of list) m.set(ptDay(s.startsAt), [...(m.get(ptDay(s.startsAt)) ?? []), s]);
  return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
}

function SlotPicker({ slots, onPick }: { slots: DmrSlot[]; onPick: (s: DmrSlot) => void }) {
  const now = Date.now();
  const open = slots.filter((s) => s.status === 'open' && new Date(s.startsAt).getTime() > now);
  const weeks = useMemo(() => {
    const m = new Map<string, DmrSlot[]>();
    for (const s of open) m.set(weekOf(s.startsAt), [...(m.get(weekOf(s.startsAt)) ?? []), s]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [open]);
  const [week, setWeek] = useState<string | null>(null);
  const current = weeks.find(([w]) => w === week) ?? weeks[0];
  if (!current) return <p style={{ color: MUTED }}>No open times left. Email Marketing@amadafranchise.com and we’ll find one.</p>;

  return (
    <div>
      <WeekChips
        weeks={weeks.map(([w, list]) => ({
          week: w,
          days: groupByDay(list).map(([d]) => d),
          detail: `${list.length} open time${list.length === 1 ? '' : 's'}`,
        }))}
        current={current[0]}
        onPick={setWeek}
      />
      <TimeZoneNote />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {groupByDay(current[1]).map(([day, list]) => (
          <section key={day} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden', background: dayTint(day) }}>
            <DayHeader day={day} right={`${list.length} open`} />
            <div style={{ display: 'grid', gap: 6, padding: 10 }}>
              {list
                .sort((a, b) => a.startsAt.localeCompare(b.startsAt) || a.teamMember.name.localeCompare(b.teamMember.name))
                .map((s) => (
                  <button key={s.id} onClick={() => onPick(s)} style={slotBtn}>
                    <strong>{fmtWhen(s.startsAt, { withDay: false })}</strong>
                    <span style={{ color: MUTED }}>with {s.teamMember.name}</span>
                  </button>
                ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function AttendeesEditor({ value, onChange }: { value: DmrAttendee[]; onChange: (v: DmrAttendee[]) => void }) {
  const set = (i: number, patch: Partial<DmrAttendee>) => onChange(value.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  return (
    <div style={{ fontSize: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Staff or co-owners to invite (optional)</div>
      <div style={{ color: MUTED, fontSize: 13, marginBottom: 8 }}>
        For example, an office manager who asks for reviews, or whoever posts on social media.
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {value.map((a, i) => (
          <div key={i} style={{ display: 'flex', gap: 6 }}>
            <input value={a.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Name" style={{ ...input, flex: '1 1 40%' }} />
            <input value={a.email} onChange={(e) => set(i, { email: e.target.value })} placeholder="Email" type="email" style={{ ...input, flex: '1 1 60%' }} />
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} style={linkBtn} aria-label="Remove">
              remove
            </button>
          </div>
        ))}
      </div>
      {value.length < 10 && (
        <button onClick={() => onChange([...value, { name: '', email: '' }])} style={{ ...linkBtn, marginTop: 6 }}>
          + Add a person
        </button>
      )}
    </div>
  );
}

// ── Schedule ───────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, { text: string; bg: string; fg: string }> = {
  open: { text: 'Open', bg: '#f1f5f9', fg: MUTED },
  blocked: { text: 'Blocked', bg: '#f1f5f9', fg: '#94a3b8' },
  scheduled: { text: 'Booked', bg: '#e0e7ff', fg: '#3730a3' },
  completed: { text: 'Done', bg: '#dcfce7', fg: '#15803d' },
  no_show: { text: 'No-show', bg: '#fee2e2', fg: '#b91c1c' },
};

function Pill({ k }: { k: string }) {
  const s = STATUS_LABEL[k] ?? STATUS_LABEL.open;
  return <span style={{ background: s.bg, color: s.fg, borderRadius: 999, padding: '2px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{s.text}</span>;
}

function ScheduleBoard({ role }: { role: 'admin' | 'fbc' | 'franchisee' }) {
  const { slots, partners } = useDmrData();
  const invalidate = useInvalidateDmr();
  const [week, setWeek] = useState<string | null>(null);
  const [showOpen, setShowOpen] = useState(false);
  const [moving, setMoving] = useState<DmrBooking | null>(null);

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => invalidate(),
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });

  const all = slots.data?.slots ?? [];
  const weeks = useMemo(() => {
    const m = new Map<string, { booked: number; total: number; days: Set<string> }>();
    for (const s of all) {
      const w = weekOf(s.startsAt);
      const e = m.get(w) ?? { booked: 0, total: 0, days: new Set<string>() };
      e.total++;
      e.days.add(ptDay(s.startsAt));
      if (s.booking) e.booked++;
      m.set(w, e);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [all]);

  if (slots.isLoading) return <p style={{ color: MUTED }}>Loading schedule…</p>;
  if (weeks.length === 0) return <p style={{ color: MUTED }}>No call slots yet. Marketing creates them from Setup.</p>;

  const current = week ?? weeks.find(([, e]) => e.booked < e.total)?.[0] ?? weeks[0][0];
  const bookingFor = (id: number) => partners.data?.partners.find((p) => p.booking?.id === id)?.booking ?? null;

  if (moving) {
    return (
      <div>
        <p>
          Moving <strong>{moving.partnerName}</strong> from {moving.startsAt ? fmtWhen(moving.startsAt) : '—'}.{' '}
          <button onClick={() => setMoving(null)} style={linkBtn}>back to the schedule</button>
        </p>
        <BookPanel role={role} reschedule={moving} onDone={() => setMoving(null)} />
      </div>
    );
  }

  const weekSlots = all.filter((s) => weekOf(s.startsAt) === current);
  return (
    <div>
      <WeekChips
        weeks={weeks.map(([w, e]) => ({ week: w, days: [...e.days].sort(), detail: `${e.booked} of ${e.total} booked` }))}
        current={current}
        onPick={setWeek}
      />
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
        <label style={{ fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={showOpen} onChange={(e) => setShowOpen(e.target.checked)} />
          Show open slots
        </label>
      </div>
      <TimeZoneNote />
      <div style={{ display: 'grid', gap: 18 }}>
        {groupByDay(weekSlots).map(([day, daySlots]) => {
          const booked = daySlots.filter((s) => s.booking).length;
          const rows = daySlots
            .filter((s) => showOpen || s.booking || s.status === 'blocked')
            .sort((a, b) => a.startsAt.localeCompare(b.startsAt) || a.teamMember.name.localeCompare(b.teamMember.name));
          return (
            <section key={day} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
              <DayHeader day={day} right={`${booked} of ${daySlots.length} booked`} />
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr>
                      {['Time', 'Team', 'Franchise partner', 'FBC', 'Status', 'Invite', ''].map((h) => (
                        <th key={h} style={{ ...th, position: 'static', background: dayTint(day) }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => {
                      const b = s.booking;
                      return (
                        <tr key={s.id}>
                          <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtWhen(s.startsAt, { withDay: false })}</td>
                          <td style={td}>{s.teamMember.name}</td>
                          <td style={td}>
                            {b ? (
                              <>
                                <Link to={`/dmr/call/${b.id}`} style={{ color: NAVY, fontWeight: 700 }}>{b.partnerName}</Link>
                                <div style={{ color: MUTED, fontSize: 12 }}>{b.afs.join(', ')}</div>
                              </>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>{s.status === 'blocked' ? s.blockedReason || 'Blocked' : '—'}</span>
                            )}
                          </td>
                          <td style={td}>{b?.fbcName ?? ''}</td>
                          <td style={td}><Pill k={b ? b.status : s.status} /></td>
                          <td style={td}>
                            {b && (
                              <span style={{ color: b.inviteStatus === 'sent' ? '#15803d' : b.inviteStatus === 'failed' ? '#b91c1c' : '#b45309', fontSize: 13 }}>
                                {b.inviteStatus === 'sent' ? 'Sent' : b.inviteStatus === 'failed' ? 'Failed' : 'Waiting'}
                              </span>
                            )}
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            {b && (
                              <Link to={`/dmr/call/${b.id}`} style={{ ...linkBtn, textDecoration: 'none' }}>
                                {b.status === 'scheduled' ? 'Run call' : 'Open'}
                              </Link>
                            )}
                            {b && b.status === 'scheduled' && (
                              <>
                                <button style={linkBtn} onClick={() => { const full = bookingFor(b.id); if (full) setMoving(full); }}>
                                  Reschedule
                                </button>
                                <button
                                  style={linkBtn}
                                  onClick={() => window.confirm(`Cancel ${b.partnerName}'s call? Everyone on the invite gets a cancellation.`) && act.mutate(() => dmrApi.cancel(b.id))}
                                >
                                  Cancel
                                </button>
                              </>
                            )}
                            {b && role === 'admin' && (
                              <>
                                {b.status !== 'completed' && <button style={linkBtn} onClick={() => act.mutate(() => dmrApi.update(b.id, { status: 'completed' }))}>Done</button>}
                                {b.status !== 'no_show' && <button style={linkBtn} onClick={() => act.mutate(() => dmrApi.update(b.id, { status: 'no_show' }))}>No-show</button>}
                                {b.inviteStatus !== 'sent' && b.status === 'scheduled' && (
                                  <button style={linkBtn} onClick={() => act.mutate(() => dmrApi.resendInvite(b.id))}>Send invite</button>
                                )}
                              </>
                            )}
                            {!b && role === 'admin' && (
                              <button
                                style={linkBtn}
                                onClick={() => {
                                  if (s.status === 'blocked') act.mutate(() => dmrApi.setSlot(s.id, { status: 'open' }));
                                  else {
                                    const reason = window.prompt('Block this slot? Reason (e.g. PTO):', 'Out of office');
                                    if (reason !== null) act.mutate(() => dmrApi.setSlot(s.id, { status: 'blocked', reason }));
                                  }
                                }}
                              >
                                {s.status === 'blocked' ? 'Unblock' : 'Block'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ ...td, color: MUTED }}>
                          Nothing booked on {dayName(day)} yet.{' '}
                          {!showOpen && <button style={linkBtn} onClick={() => setShowOpen(true)}>Show open slots</button>}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ── Partners ───────────────────────────────────────────────────────────────

function PartnersTable({ role }: { role: 'admin' | 'fbc' | 'franchisee' }) {
  const { partners } = useDmrData();
  const [filter, setFilter] = useState<'all' | 'mine' | 'unbooked'>(role === 'fbc' ? 'mine' : 'all');
  const [q, setQ] = useState('');
  const [booking, setBooking] = useState<DmrPartner | null>(null);

  if (partners.isLoading) return <p style={{ color: MUTED }}>Loading partners…</p>;
  if (booking) {
    return (
      <div>
        <p>
          Booking <strong>{booking.name}</strong> ({booking.afs.join(', ')}).{' '}
          <button onClick={() => setBooking(null)} style={linkBtn}>back to the list</button>
        </p>
        <BookPanel role={role} fixedPartner={booking} onDone={() => setBooking(null)} />
      </div>
    );
  }
  const all = partners.data?.partners ?? [];
  const list = all.filter((p) => {
    if (filter === 'mine' && !p.mine) return false;
    if (filter === 'unbooked' && p.booking) return false;
    if (!q.trim()) return true;
    return `${p.name} ${p.afs.join(' ')} ${p.fbc?.name ?? ''}`.toLowerCase().includes(q.trim().toLowerCase());
  });
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {(role === 'fbc' ? (['mine', 'unbooked', 'all'] as const) : (['all', 'unbooked'] as const)).map((k) => (
          <button key={k} onClick={() => setFilter(k)} style={chip(filter === k)}>
            {k === 'mine' ? 'My partners' : k === 'unbooked' ? 'Not booked yet' : 'All partners'}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ ...input, maxWidth: 240 }} />
      </div>
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'auto', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>{['Franchise partner', 'AFs', 'FBC', 'DMR call', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.key}>
                <td style={td}><strong>{p.name}</strong>{p.marketingNames[0] && <div style={{ color: MUTED, fontSize: 12 }}>{p.marketingNames[0]}</div>}</td>
                <td style={td}>{p.afs.join(', ')}</td>
                <td style={td}>{p.fbc?.name ?? <span style={{ color: '#94a3b8' }}>none</span>}</td>
                <td style={td}>
                  {p.booking ? (
                    <>
                      <Pill k={p.booking.status} />{' '}
                      {p.booking.startsAt && <span style={{ color: MUTED }}>{fmtWhen(p.booking.startsAt)} · {p.booking.teamMember?.name}</span>}
                    </>
                  ) : (
                    <span style={{ color: '#b45309' }}>Not booked</span>
                  )}
                </td>
                <td style={td}>
                  {p.booking
                    ? <Link to={`/dmr/call/${p.booking.id}`} style={{ ...linkBtn, textDecoration: 'none' }}>Open</Link>
                    : <button style={linkBtn} onClick={() => setBooking(p)}>Book</button>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} style={{ ...td, color: MUTED }}>No partners match.</td></tr>}
          </tbody>
        </table>
      </div>
      <p style={{ color: MUTED, fontSize: 13 }}>{list.length} of {all.length} partners shown.</p>
    </div>
  );
}

// ── Action items (staff) ───────────────────────────────────────────────────

function ActionItemsTab({ role }: { role: 'admin' | 'fbc' | 'franchisee' }) {
  const [openOnly, setOpenOnly] = useState(true);
  const [side, setSide] = useState<'all' | 'amada' | 'franchisee'>('all');
  const q = useQuery({ queryKey: ['dmr', 'action-items', openOnly], queryFn: () => dmrApi.actionItems(openOnly) });
  if (q.isLoading) return <p style={{ color: MUTED }}>Loading action items…</p>;
  const items = (q.data?.items ?? []).filter((i) => side === 'all' || i.ownerSide === side);
  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {(['all', 'amada', 'franchisee'] as const).map((k) => (
          <button key={k} onClick={() => setSide(k)} style={chip(side === k)}>
            {k === 'all' ? 'Everyone' : k === 'amada' ? 'Amada Marketing' : 'Franchise partners'}
          </button>
        ))}
        <label style={{ fontSize: 14, display: 'flex', gap: 6, alignItems: 'center', marginLeft: 8 }}>
          <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
          Open only
        </label>
      </div>
      <DmrActionItems items={items} mode="list" canEdit={role === 'admin'} showPartner />
    </div>
  );
}

// ── Setup (admin) ──────────────────────────────────────────────────────────

function SetupPanel({ data }: { data: DmrSeasonResponse }) {
  const qc = useQueryClient();
  const team = useQuery({ queryKey: ['dmr', 'team'], queryFn: dmrApi.team });
  const reminder = useQuery({ queryKey: ['dmr', 'reminder'], queryFn: dmrApi.reminderPlan });
  const [emails, setEmails] = useState<Record<number, string>>({});
  const run = useMutation({
    mutationFn: async (fn: () => Promise<string>) => fn(),
    onSuccess: (msg) => {
      showToast(msg);
      qc.invalidateQueries({ queryKey: ['dmr'] });
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });

  const AUDIENCE: Array<[DmrAudience, string]> = [
    ['admin', 'Marketing only (testing)'],
    ['fbc', 'Marketing + FBCs'],
    ['all', 'Everyone, including franchisees'],
  ];

  return (
    <div style={{ display: 'grid', gap: 18, maxWidth: 820 }}>
      <Card title="Who can book">
        <p style={hint}>Hold at “Marketing only” until calendar invites are switched on.</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {AUDIENCE.map(([k, label]) => (
            <button
              key={k}
              style={chip(data.season.audience === k)}
              onClick={() =>
                k !== data.season.audience &&
                window.confirm(`Change who can book to “${label}”?`) &&
                run.mutate(async () => (await dmrApi.setAudience(k), `Now open to: ${label}`))
              }
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Team and call slots">
        <p style={hint}>
          The email is the Outlook calendar the invite is created on. Start times are Pacific (Eastern in
          brackets), on Tuesdays and Thursdays; click one to turn it on or off for that person.
        </p>
        <table style={{ borderCollapse: 'collapse', fontSize: 14, width: '100%' }}>
          <tbody>
            {(team.data?.team ?? []).map((m) => (
              <tr key={m.id}>
                <td style={{ ...td, width: 110 }}><strong>{m.name}</strong></td>
                <td style={{ ...td, minWidth: 250 }}>
                  <input
                    value={emails[m.id] ?? m.email ?? ''}
                    onChange={(e) => setEmails({ ...emails, [m.id]: e.target.value })}
                    placeholder="first.last@amadafranchise.com"
                    style={input}
                  />
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(team.data?.slotTimes ?? []).map((t) => {
                      const on = m.times.includes(t);
                      return (
                        <button
                          key={t}
                          title={on ? 'Click to stop offering this time' : 'Click to offer this time'}
                          onClick={() => {
                            const next = on ? m.times.filter((x) => x !== t) : [...m.times, t];
                            const msg = on
                              ? `Stop offering ${fmtPt(t)} for ${m.name}? Their open slots at that time are removed; booked calls stay.`
                              : `Offer ${fmtPt(t)} for ${m.name}?`;
                            if (!window.confirm(msg)) return;
                            run.mutate(async () => {
                              const r = await dmrApi.setTimes(m.id, next);
                              return `${m.name}: ${r.added} slots added, ${r.removed} removed`;
                            });
                          }}
                          style={{
                            border: `1px solid ${on ? NAVY : '#cbd5e1'}`, background: on ? NAVY : '#fff', color: on ? '#fff' : MUTED,
                            borderRadius: 999, padding: '3px 9px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          {fmtPt(t)}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td style={{ ...td, color: MUTED, whiteSpace: 'nowrap' }}>{m.slots} slots</td>
                <td style={td}>
                  {emails[m.id] !== undefined && emails[m.id] !== (m.email ?? '') && (
                    <button style={linkBtn} onClick={() => run.mutate(async () => (await dmrApi.updateTeam(m.id, { email: emails[m.id] }), `Saved ${m.name}’s email`))}>
                      Save
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button style={{ ...primaryBtn, marginTop: 10 }} onClick={() => run.mutate(async () => `Created ${(await dmrApi.generateSlots()).created} new slots`)}>
          Create slots
        </button>
      </Card>

      <Card title="Calendar invites">
        <p style={hint}>
          {data.invites.live
            ? 'Invites are live. Send any that were waiting or failed.'
            : data.invites.reason}
        </p>
        <button
          style={{ ...primaryBtn, ...(!data.invites.live && { opacity: 0.45, cursor: 'not-allowed' }) }}
          disabled={!data.invites.live}
          onClick={() =>
            run.mutate(async () => {
              const r = await dmrApi.sendPendingInvites();
              return `Sent ${r.sent}, failed ${r.failed}, still waiting ${r.pending}`;
            })
          }
        >
          Send waiting invites
        </button>
      </Card>

      <Card title="FBC reminder">
        <p style={hint}>
          Checked automatically. When fewer than {data.season.runwayAlertWeeks} weeks are booked ahead, each FBC
          gets a list of their unbooked partners (at most once a week), and you and Rick get a summary.
        </p>
        {reminder.data && (
          <>
            <p style={{ margin: '0 0 6px', fontSize: 14 }}>
              <strong>{reminder.data.send ? 'Would send now.' : 'Would not send now.'}</strong> {reminder.data.reason}
            </p>
            <ul style={{ margin: 0, fontSize: 14, color: MUTED }}>
              {reminder.data.perFbc.map((f) => <li key={f.email}>{f.name}: {f.unbooked} not booked</li>)}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: '14px 16px', background: '#fff' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16, color: NAVY }}>{title}</h3>
      {children}
    </section>
  );
}

// ── Franchisee view ────────────────────────────────────────────────────────

function MyCall() {
  const { partners } = useDmrData();
  const items = useQuery({ queryKey: ['dmr', 'action-items', 'mine'], queryFn: () => dmrApi.actionItems(false) });
  const invalidate = useInvalidateDmr();
  const [moving, setMoving] = useState(false);
  const [editing, setEditing] = useState<DmrAttendee[] | null>(null);
  const cancel = useMutation({
    mutationFn: (id: number) => dmrApi.cancel(id),
    onSuccess: () => {
      showToast('Your call is cancelled. Pick a new time whenever you’re ready.');
      invalidate();
    },
    onError: (e) => showToast(errText(e), { variant: 'error' }),
  });
  const saveAttendees = useMutation({
    mutationFn: ({ id, list }: { id: number; list: DmrAttendee[] }) =>
      dmrApi.update(id, { extraAttendees: list.filter((a) => a.email.trim()) }),
    onSuccess: (res) => {
      inviteToast(res.invite, 'Saved');
      setEditing(null);
      invalidate();
    },
    onError: (e) => showToast(errText(e), { variant: 'error', durationMs: 5000 }),
  });

  if (partners.isLoading) return <p style={{ color: MUTED }}>Loading…</p>;
  const mine = partners.data?.partners ?? [];
  if (mine.length === 0) {
    return <p style={{ color: MUTED }}>We couldn’t match your login to a franchise. Email Marketing@amadafranchise.com and we’ll book you.</p>;
  }

  return (
    <div style={{ display: 'grid', gap: 18, marginTop: 18 }}>
      {mine.map((p) => {
        const b = p.booking;
        if (!b) {
          return (
            <div key={p.key}>
              <p style={{ fontSize: 15 }}>
                Pick a time for your annual review. We’ll go over your leads, reviews, dashboard data and SEO/PPC
                together, and you’re welcome to bring staff.
              </p>
              <BookPanel role="franchisee" fixedPartner={p} />
            </div>
          );
        }
        if (moving && b.status === 'scheduled') {
          return (
            <div key={p.key}>
              <p>
                Moving your call from {b.startsAt ? fmtWhen(b.startsAt) : '—'}.{' '}
                <button style={linkBtn} onClick={() => setMoving(false)}>keep the current time</button>
              </p>
              <BookPanel role="franchisee" reschedule={b} onDone={() => setMoving(false)} />
            </div>
          );
        }
        return (
          <div key={p.key} style={{ display: 'grid', gap: 18 }}>
          <Card title={b.status === 'completed' ? 'Your review is done. Thank you!' : 'Your Digital Marketing Review'}>
            {b.startsAt && (
              <p style={{ fontSize: 18, margin: '0 0 4px', color: NAVY }}>
                <strong>{fmtWhen(b.startsAt)}</strong>
              </p>
            )}
            <p style={{ margin: '0 0 10px', color: MUTED }}>
              90 minutes with {b.teamMember?.name ?? 'the marketing team'} · {b.afs.join(', ')}
            </p>
            {b.teamsJoinUrl && b.status === 'scheduled' && (
              <p><a href={b.teamsJoinUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>Join on Microsoft Teams</a></p>
            )}
            {b.status === 'scheduled' && (
              <>
                {editing ? (
                  <div style={{ margin: '10px 0' }}>
                    <AttendeesEditor value={editing} onChange={setEditing} />
                    <button style={{ ...primaryBtn, marginTop: 10 }} onClick={() => saveAttendees.mutate({ id: b.id, list: editing })}>
                      Save and update the invite
                    </button>{' '}
                    <button style={linkBtn} onClick={() => setEditing(null)}>never mind</button>
                  </div>
                ) : (
                  <p style={{ fontSize: 14 }}>
                    Also invited:{' '}
                    {b.extraAttendees.length ? b.extraAttendees.map((a) => a.name || a.email).join(', ') : 'no one yet'}{' '}
                    <button style={linkBtn} onClick={() => setEditing(b.extraAttendees)}>edit</button>
                  </p>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button style={linkBtn} onClick={() => setMoving(true)}>Reschedule</button>
                  <button
                    style={{ ...linkBtn, color: '#b91c1c' }}
                    onClick={() => window.confirm('Cancel your DMR call?') && cancel.mutate(b.id)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </Card>
          {(() => {
            const mineItems = (items.data?.items ?? []).filter((i) => i.bookingId === b.id);
            if (mineItems.length === 0) return null;
            return (
              <Card title="Action items from your review">
                <p style={hint}>Tick off yours as you finish them. Marketing updates theirs.</p>
                <DmrActionItems items={mineItems} mode="franchisee" />
              </Card>
            );
          })()}
          </div>
        );
      })}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const input: React.CSSProperties = {
  padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  background: NAVY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, padding: '0 6px', fontWeight: 600,
};
const slotBtn: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 12px', border: '1px solid #cbd5e1',
  borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 14, color: NAVY, textAlign: 'left',
};
const th: React.CSSProperties = {
  textAlign: 'left', padding: '9px 12px', borderBottom: `1px solid ${BORDER}`, fontSize: 12, textTransform: 'uppercase',
  letterSpacing: '.05em', color: MUTED, background: '#fafafa', position: 'sticky', top: 0,
};
const td: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };
const hint: React.CSSProperties = { margin: '0 0 10px', color: MUTED, fontSize: 14 };
function chip(on: boolean): React.CSSProperties {
  return {
    border: on ? `2px solid ${NAVY}` : '1px solid #cbd5e1', background: on ? NAVY : '#fff', color: on ? '#fff' : NAVY,
    borderRadius: 999, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13,
  };
}
